<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';

import CampaignLayout from 'dashboard/components-next/Campaigns/CampaignLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';
import BlacklistTable from 'dashboard/components-next/Campaigns/Advanced/Blacklist/BlacklistTable.vue';
import AddToBlacklistDialog from 'dashboard/components-next/Campaigns/Advanced/Blacklist/AddToBlacklistDialog.vue';

import CampaignBlacklistsAPI from 'dashboard/api/campaignBlacklists';

const { t } = useI18n();

const blacklists = ref([]);
const isLoading = ref(false);
const searchQuery = ref('');
const currentPage = ref(1);

// Dialog ref
const addDialogRef = ref(null);

const filteredBlacklists = computed(() => {
  if (!searchQuery.value) return blacklists.value;
  const query = searchQuery.value.toLowerCase();
  return blacklists.value.filter(
    b =>
      b.phone_number.includes(query) || b.reason?.toLowerCase().includes(query)
  );
});

const fetchBlacklists = async (page = 1) => {
  isLoading.value = true;
  try {
    const response = await CampaignBlacklistsAPI.get({ page, per_page: 25 });
    blacklists.value = response.data?.payload || [];
    currentPage.value = page;
  } catch (error) {
    useAlert(t('CAMPAIGN.BLACKLIST.FETCH_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const openAddDialog = () => {
  addDialogRef.value?.open();
};

const handleAdd = async data => {
  try {
    await CampaignBlacklistsAPI.create(data);
    useAlert(t('CAMPAIGN.BLACKLIST.ADD_SUCCESS'));
    fetchBlacklists();
  } catch (error) {
    useAlert(t('CAMPAIGN.BLACKLIST.ADD_ERROR'));
  }
};

const handleDelete = async entry => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('CAMPAIGN.BLACKLIST.DELETE_CONFIRM'))) return;

  try {
    await CampaignBlacklistsAPI.delete(entry.id);
    useAlert(t('CAMPAIGN.BLACKLIST.DELETE_SUCCESS'));
    fetchBlacklists();
  } catch (error) {
    useAlert(t('CAMPAIGN.BLACKLIST.DELETE_ERROR'));
  }
};

const handleExport = async () => {
  try {
    const response = await CampaignBlacklistsAPI.export();
    const blob = new Blob([response.data], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `blacklist_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    useAlert(t('CAMPAIGN.BLACKLIST.EXPORT_ERROR'));
  }
};

onMounted(() => {
  fetchBlacklists();
});
</script>

<template>
  <CampaignLayout
    :header-title="t('CAMPAIGN.BLACKLIST.TITLE')"
    :button-label="t('CAMPAIGN.BLACKLIST.ADD')"
    @click="openAddDialog"
  >
    <template #left-action>
      <Button
        variant="faded"
        color="slate"
        icon="i-lucide-download"
        size="sm"
        @click="handleExport"
      >
        {{ t('CAMPAIGN.BLACKLIST.EXPORT') }}
      </Button>
    </template>

    <div class="space-y-4">
      <Input
        v-model="searchQuery"
        :placeholder="t('CAMPAIGN.BLACKLIST.SEARCH_PH')"
        icon="i-lucide-search"
      />

      <div v-if="isLoading" class="flex justify-center py-10">
        <Spinner />
      </div>

      <template v-else>
        <BlacklistTable
          v-if="filteredBlacklists.length"
          :blacklists="filteredBlacklists"
          @delete="handleDelete"
        />

        <div v-else class="py-12 text-center">
          <div class="i-lucide-ban mx-auto size-12 text-n-slate-8" />
          <h3 class="mt-4 text-sm font-medium text-n-slate-12">
            {{ t('CAMPAIGN.BLACKLIST.EMPTY') }}
          </h3>
          <p class="mt-1 text-sm text-n-slate-11">
            {{ t('CAMPAIGN.BLACKLIST.EMPTY_HINT') }}
          </p>
          <Button
            class="mt-4"
            icon="i-lucide-plus"
            :label="t('CAMPAIGN.BLACKLIST.ADD_NUMBER')"
            @click="openAddDialog"
          />
        </div>
      </template>

      <PaginationFooter
        v-if="filteredBlacklists.length > 0"
        :current-page="currentPage"
        :total-count="blacklists.length"
        :page-size="25"
        @page-change="fetchBlacklists"
      />
    </div>

    <!-- Add Dialog -->
    <AddToBlacklistDialog
      ref="addDialogRef"
      @close="() => {}"
      @submit="handleAdd"
    />
  </CampaignLayout>
</template>
